import styles from './EditableEntityTable.module.scss';

import { Delete, Save } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { sortBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IdType, TableInstance } from 'react-table';

import { useDialogs } from '@work4all/components';
import { EntityLike } from '@work4all/components/lib/components/entity-picker/types';

import {
  useDataMutation,
  useDataProvider,
  useDeleteEntity,
} from '@work4all/data';

import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EditTable } from '../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/EditTable';
import {
  DropZoneType,
  EditTableColumns,
  OnEditPosition,
} from '../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/types';

export interface TableFunctions<T> {
  handleEdit: (e: OnEditPosition<T>) => Promise<void>;
}

interface EditableEntityTableProps<T> {
  idType: 'number' | 'string';
  dataFields: T;
  newEntryMandatoryFields: (keyof T)[];
  sortByField?: keyof T;
  dataProviderProps?: Pick<
    DataRequest,
    'sort' | 'vars' | 'completeDataResponse'
  >;
  entity: Entities;
  deleteEntityType?: Entities;
  columns: EditTableColumns<T>[];
  toolbarItemsLeft?: React.ReactNode;
  enableDelete?: boolean;
  enableNewItems?: boolean;
  dragHandlerVisible?: (value: T) => boolean;
  dragHandlerClickable?: (value: T) => boolean;
  transformAfterLoading?: (value: T) => T;
  transformBeforeUpdate?: (value: T) => T;
  onInit?: (props: TableFunctions<T>) => void;
  onMovePosition?: (
    localId: string | number,
    index: number,
    dropZoneType: DropZoneType
  ) => void;
  handleOrderChange?: (columns: IdType<T>[]) => IdType<T>[];
}

const NEW_ITEM_CODE = -1;
const NEW_ITEM_ID = '-1';

// const correctTypes

export const EditableEntityTable = <T extends EntityLike>(
  props: EditableEntityTableProps<T>
) => {
  const {
    dataFields,
    entity,
    idType,
    newEntryMandatoryFields,
    dataProviderProps,
    columns,
    toolbarItemsLeft,
    sortByField,
    enableDelete,
    transformBeforeUpdate,
    onInit,
    onMovePosition,
    handleOrderChange,
    transformAfterLoading,
    dragHandlerVisible,
    dragHandlerClickable,
    enableNewItems = true,
    deleteEntityType,
  } = props;
  const tableInstanceRef = useRef<TableInstance<T>>(null);

  const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);

  const dialogs = useDialogs();

  const newItemId = useMemo(
    () => (idType === 'number' ? NEW_ITEM_CODE : NEW_ITEM_ID),
    [idType]
  );

  const { t } = useTranslation();

  const req: DataRequest = useMemo<DataRequest>(
    () => ({
      data: dataFields,
      entity,
      ...dataProviderProps,
    }),
    [dataProviderProps, entity, dataFields]
  );

  const { data } = useDataProvider(req);

  const [newEntry, setNewEntry] = useState<T>({} as T);

  const transformedData = useMemo<T[]>(() => {
    let result = [...data];

    if (transformAfterLoading) {
      result = result.map(transformAfterLoading);
    }

    result = result.map((item) => ({ ...item, localId: item.id }));

    if (enableNewItems) {
      result.push({
        id: newItemId,
        localId: newItemId,
      });
    }

    if (sortByField) {
      return sortBy(result, (item) => {
        const value = item[sortByField];
        return typeof value === 'string' ? value.toLowerCase() : value;
      });
    }

    return result;
  }, [data, sortByField, transformAfterLoading, enableNewItems, newItemId]);

  const [mutate] = useDataMutation({
    entity,
    mutationType: EMode.upsert,
    responseData: dataFields,
  });

  const [deleteEntity] = useDeleteEntity();

  const mandatoryFieldsFilled = useMemo(() => {
    return newEntryMandatoryFields.every((key) =>
      typeof newEntry[key] === 'string'
        ? newEntry[key].length > 0
        : newEntry[key] !== undefined
    );
  }, [newEntry, newEntryMandatoryFields]);

  const [errorMessage, setErrorMessage] = useState('');

  const handleInsertNew = useCallback(async () => {
    setErrorMessage('');
    if (!mandatoryFieldsFilled) {
      const mandatoryFieldNames = columns
        .filter(
          (col) =>
            typeof col.accessor === 'string' &&
            newEntryMandatoryFields.includes(col.accessor as keyof T)
        )
        .map((col) => col.Header);

      setErrorMessage(
        `${t('COMMON.MANDATORY_FIELD', {
          count: mandatoryFieldNames.length,
        })}: ${mandatoryFieldNames.join(', ')}`
      );
      return;
    }
    await mutate(
      transformBeforeUpdate ? transformBeforeUpdate(newEntry) : newEntry
    );
    setNewEntry({} as T);
  }, [
    mandatoryFieldsFilled,
    mutate,
    newEntry,
    columns,
    t,
    newEntryMandatoryFields,
    transformBeforeUpdate,
  ]);

  const handleEdit = useCallback(
    async (e: OnEditPosition<T>) => {
      const isNewEntry = e.position.id === newItemId;
      if (isNewEntry) {
        const newEntryUpdated = { ...newEntry, ...e.position, id: undefined };

        if (Object.keys(e.position).length > 2) setNewEntry(newEntryUpdated);

        return;
      }

      /**
       * Check if any fields exist beside id and localId
       */

      if (
        Object.keys(e.position).length > 2 &&
        Object.keys(e.position).every((key) => e.position[key] !== undefined)
      ) {
        mutate(
          transformBeforeUpdate ? transformBeforeUpdate(e.position) : e.position
        );
      }
    },
    [mutate, newEntry, newItemId, transformBeforeUpdate]
  );

  const handleAddLookUp = useCallback(() => {}, []);

  const handleDelete = useCallback(() => {
    dialogs.confirm({
      title: t('COMMON.DELETE_ENTRY_TITLE'),
      description: t('COMMON.DELETE_ENTRY.DESCRIPTION'),
      confirmLabel: t('COMMON.DELETE'),
      cancelLabel: t('COMMON.ABORT'),
      onConfirm: () => {
        deleteEntity({ ids: selectedIds, type: deleteEntityType || entity });
        setSelectedIds([]);
      },
    });
  }, [dialogs, t, deleteEntity, selectedIds, deleteEntityType, entity]);

  const handleSelectedItemIdsChange = useCallback(
    (selectedPositionIds: string[]) =>
      setSelectedIds(
        selectedPositionIds
          .map((id) => (idType === 'number' ? Number(id) : id))
          .filter((id) => Number(id) > 0)
      ),
    [idType]
  );

  useEffect(() => {
    onInit?.({
      handleEdit,
    });
  }, [handleEdit, onInit]);

  return (
    <Stack maxHeight="100%" overflow="hidden">
      <Stack direction="row" p="1rem" gap="1rem">
        {entity === Entities.lookUp && (
          <IconButton onClick={handleAddLookUp}>
            <AddIcon />
          </IconButton>
        )}
        {toolbarItemsLeft}
        {enableDelete && (
          <IconButton
            onClick={handleDelete}
            disabled={selectedIds.length === 0}
            color="primary"
          >
            <Delete />
          </IconButton>
        )}
      </Stack>
      <Stack
        direction="row"
        flex={1}
        maxHeight="100%"
        overflow="hidden"
        style={{ overflowX: 'auto' }}
      >
        <Box borderTop="1px solid var(--ui04)" width="2rem"></Box>
        <Box>
          <EditTable<T>
            columns={columns}
            items={transformedData}
            tableInstanceRef={tableInstanceRef}
            fitSpace
            updateMode="cell"
            onEditPosition={handleEdit}
            rowSizeMode="SINGLE"
            onSelectedItemIdsChange={handleSelectedItemIdsChange}
            onMovePosition={onMovePosition}
            handleOrderChange={handleOrderChange}
            dragHandlerVisible={dragHandlerVisible}
            dragHandlerClickable={dragHandlerClickable}
            classes={{
              innerInfiniteTable: styles.infiniteTable,
              headerRow: styles.headerRow,
              tableRow: styles.tableRow,
            }}
          />
        </Box>
        <Box borderTop="1px solid var(--ui04)" flex={1}></Box>
      </Stack>
      {enableNewItems && (
        <Stack
          padding="0.5rem 2rem"
          gap="1rem"
          direction="row"
          alignItems="center"
        >
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleInsertNew}
            disabled={!mandatoryFieldsFilled}
          >
            {t('INPUTS.SAVE')}
          </Button>
          <Typography color="error" variant="caption">
            {errorMessage}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};
